import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, from as observableFrom, of as observableOf } from 'rxjs';
import {
  catchError,
  map,
  mergeMap,
  reduce,
  toArray,
  windowCount,
} from 'rxjs/operators';
import * as URI from 'urijs';

import {
  OnlineResourceGoogleBooks,
  OnlineResourceGoogleBooksType,
} from '®shared/bibliographic-resource';

import { GoogleBooksResponse } from './google-books-response';

/**
 * BUFFER_SIZE here controls the number of ISBNs requested in a single batch from Google Books.
 *
 * The maths behind this are:
 *
 * - we need to encode all the data on the GET request URL
 * - the maximum acceptable URL is a vague number, so assuming 1000 characters
 * - each ISBN will be 10 or 13 characters, so assume they're all 13.
 * - small amount of preamble (protocol/host; query params; JSONP callback), ~100 chars
 * - (1000 - 100) / 13 = ~70
 *
 * So we will limit to 70 ISBNs per GET request.
 **/
const BUFFER_SIZE = 70;

@Injectable()
export class GoogleBooksService {
  constructor(private httpClient: HttpClient) {}

  /**
   * Calls the Google Books API to find preview availability for resources, identified by a string of ISBNs.
   *
   * @param {string[]} isbns - the resource identifier ISBNs
   * @returns {Observable<GoogleBooksResponse>} the response from Google Books.
   **/
  getPreviewDetailsForIsbns(isbns: string[]): Observable<GoogleBooksResponse> {
    if (!isbns.length) {
      return observableOf({});
    }

    const googleBooksUrl = new URI('https://books.google.com')
      .segment('books')
      .search({
        bibkeys: isbns.join(','),
        jscmd: 'viewapi',
      })
      .toString();

    return this.httpClient.jsonp<GoogleBooksResponse>(
      googleBooksUrl,
      'callback',
    );
  }

  /**
   * Breaks up a list of ISBNs into batches, calls the Google Books API for each batch,
   * and merge the resultant response objects into a single response object.
   *
   * @param {string[]} isbns - the resource identifier ISBNs
   * @param {number=} bufferSize - the number of ISBNs per batch
   * @returns {Observable<GoogleBooksResponse>} a merged response from Google Books.
   **/
  getPreviewDetailsForIsbnsBatched(
    isbns: string[],
    bufferSize: number = BUFFER_SIZE,
  ): Observable<GoogleBooksResponse> {
    return observableFrom(isbns).pipe(
      windowCount(bufferSize),
      mergeMap((isbnObs: Observable<string>) =>
        isbnObs.pipe(
          toArray(),
          mergeMap(isbnBatch =>
            this.getPreviewDetailsForIsbns(isbnBatch).pipe(
              catchError(() => observableOf({})),
            ),
          ),
        ),
      ),
      reduce((soFar, response) => Object.assign({}, soFar, response), {}),
    );
  }

  getOnlineResourceForIsbns(
    isbns: string[],
  ): Observable<OnlineResourceGoogleBooks> {
    return this.getPreviewDetailsForIsbns(isbns).pipe(
      map(googleBook => {
        const googleBookIsbns = Object.keys(googleBook);
        const matchingGoogleBookIsbn = isbns.find(isbn =>
          googleBookIsbns.includes(isbn),
        );
        const matchingGoogleBook = googleBook[matchingGoogleBookIsbn];
        if (!matchingGoogleBook.embeddable) {
          throw new Error('Title is not available in Google Books');
        }

        return {
          type: OnlineResourceGoogleBooksType.GOOGLE_BOOKS,
          isbns: isbns,
          preview_url: matchingGoogleBook.preview_url,
        };
      }),
      catchError(() => observableOf(null)),
    );
  }
}
